import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  Grid,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import uploadImage from "../../../image/wmremove-transformed-Photorooms.jpg";
import {
  productAddRequest,
  productResetState,
} from "../../../redux/products/ProductAction";
import {
  handleFileChange,
  convertImageToBase64,
} from "../../constant/commonFileUpload";
import "../../../style/style.css";
import { StyledContainer, styles } from "../../components/formStyles";
import CustomButton from "../../constant/commonButton";
import ReusableFormFields from "../../constant/reusableFormFields";
import { mapBackendMessage } from "../../notification/BackendMessage";
import CommonDialog from "../../notification/Dialogbox";
import CommonSnackbar from "../../notification/Snackbar";
import { useNavigate } from "react-router-dom";
import { APP_LINK } from "../../global/MenuItems";
import { Scrollbars } from "react-custom-scrollbars-2";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { brandSearchViewAllRequest } from "../../../redux/brands/BrandAction";

const AddProduct = () => {
  const {
    handleSubmit,
    control,
    register,
    reset,
    setError,
    setValue,
    formState: { errors },
  } = useForm();

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [dialogMessage, setDialogMessage] = useState("");
  const [fileName, setFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(true);
  const fileInputRef = useRef(null);
  const [open, setOpen] = useState(false);

  const addProductState = useSelector(
    (state) => state?.product?.addProduct || {}
  );

  const brandViewAllState = useSelector(
    (state) => state?.brand?.viewAllSearchBrands
  );

  const brandList = brandViewAllState?.data?.data || [];

  useEffect(() => {
    dispatch(brandSearchViewAllRequest());
  }, [dispatch]);

  const dynamicBrandOptions = brandList
    ?.filter((brand) => brand?.status !== 2)
    .map((brand) => ({
      value: brand?.id,
      label: brand?.brand_name,
    }));

  const inputFields = [
    {
      name: "brand_id",
      label: "Brand Name",
      type: "searchable-select",
      options: dynamicBrandOptions,
      rules: { required: "Brand Name is required" },
    },
    {
      name: "product_name",
      label: "Sub Brand Name",
      size: "medium",
      rules: { required: "Sub Brand Name is required" },
    },
    {
      name: "description",
      label: "Description",
      size: "large",
      rules: { required: "Description is required" },
    },
  ];

  const handleImageUpload = (event) => {
    handleFileChange(
      event,
      setSelectedFile,
      setImagePreview,
      setFileName,
      showSnackbar
    );
  };

  useEffect(() => {
    const existingImageUrl = "https://example.com/image.jpg";
    convertImageToBase64(existingImageUrl, (base64) => {
      setSelectedFile(base64);
    });
  }, []);

  const handleImage = () => {
    if (selectedFile) {
      showSnackbar(
        `Product details saved with file: ${selectedFile.name}`,
        "success"
      );
    } else {
      showSnackbar("Please choose a file before saving.", "error");
    }
  };

  const onSubmit = (data) => {
    if (!selectedFile) {
      showSnackbar("Please upload an image before submitting.", "error");
      return;
    }
    const normalizeText = (text) => text.replace(/\s+/g, " ").trim();

    const trimmedData = {
      brand_id: normalizeText(data?.brand_id),
      product_name: normalizeText(data?.product_name),
      description: normalizeText(data?.description),
      product_image: selectedFile,
    };

    if (!trimmedData?.product_name || !trimmedData?.description) {
      showSnackbar("Product Name and Description cannot be empty.", "error");
      return;
    }

    dispatch(productAddRequest(trimmedData));
  };

  const resetFileInputs = () => {
    setSelectedFile(null);
    setImagePreview(null);
    setFileName("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleAddProductResponse = () => {
    if (addProductState?.error) {
      const errorMessage = addProductState?.errormessage;
      const errorFields = addProductState?.errorFields || {};
      if (Object.keys(errorFields).length > 0) {
        Object.keys(errorFields).forEach((field) => {
          setError(field, {
            type: "manual",
            message: errorFields[field],
          });
        });
      } else if (typeof errorMessage === "string") {
        setError("product_name", {
          type: "manual",
          message: errorMessage,
        });
      } else {
        const customMessage = mapBackendMessage(
          addProductState?.errormessage ||
            "An error occurred while updating details."
        );
        setDialogMessage(customMessage);
        setIsSuccess(false);
        setDialogOpen(true);
      }
    } else if (addProductState?.success) {
      setDialogMessage("Sub Brand Added Successfully");
      setIsSuccess(true);
      setDialogOpen(true);
      reset({
        brand_id: "",
        product_name: "",
        description: "",
      });
      resetFileInputs();
      dispatch(productResetState());
    }
  };

  useEffect(() => {
    handleAddProductResponse();
  }, [addProductState]);

  const handleCancel = () => {
    reset({
      brand_id: "",
      product_name: "",
      description: "",
    });
    resetFileInputs();
    dispatch(productResetState());
  };

  const showSnackbar = (message, severity) => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleImageClick = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={6} sm={6} md={6} lg={5} xl={5}>
            <Typography
              variant="h5"
              gutterBottom
              sx={styles.title}
              className="headingTable"
            >
              <h1> Add Sub Brand </h1>
            </Typography>
          </Grid>
          <Grid item xs={6} sm={6} md={6} lg={3} xl={3}>
            <Box sx={styles.addBackButtonDisplay}>
              <Button
                variant="contained"
                sx={styles.addBackButtonColor}
                startIcon={<ArrowBackIcon />}
                onClick={() => {
                  dispatch(productResetState());
                  navigate(APP_LINK.PRODUCTLIST);
                }}
              >
                Back
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={6} xl={5}>
            <Box>
              <Typography variant="p" gutterBottom sx={styles.photoHeading}>
                Upload Image Of Product
              </Typography>
            </Box>

            <Box>
              <StyledContainer>
                <Grid container spacing={0}>
                  <Grid item xs={12} sm={12} md={12} lg={2} xl={2}>
                    <img
                      src={imagePreview || uploadImage}
                      alt="Image"
                      width={80}
                      height={80}
                      style={styles.image}
                      onClick={handleImageClick}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={10} xl={10}>
                    <Typography
                      variant="p"
                      gutterBottom
                      sx={styles.photoContent}
                    >
                      Please upload square image, size less than 200KB
                    </Typography>
                    <Box sx={styles.fileButtonContainer}>
                      <CustomButton
                        variant="outlined"
                        component="label"
                        size="small"
                        buttonStyle="chooseFileButton"
                      >
                        Choose File
                        <input
                          type="file"
                          hidden
                          ref={fileInputRef}
                          onChange={handleImageUpload}
                        />
                      </CustomButton>
                      <CustomButton
                        variant="contained"
                        size="small"
                        buttonStyle="fileNameButton"
                        selectedFile={Boolean(selectedFile)}
                      >
                        {fileName || "No File Chosen"}
                      </CustomButton>
                    </Box>
                    <Box sx={styles.saveButtonContainer}>
                      {/* <CustomButton
                        text="Save"
                        buttonStyle="saveButton"
                        onClick={handleImage}
                        size="small"
                      >
                        Save
                      </CustomButton> */}
                    </Box>
                  </Grid>
                </Grid>
              </StyledContainer>
            </Box>
            <Box>
              <Typography variant="body2" gutterBottom sx={styles.note}>
                <span style={{ color: "red" }}>Note:</span> Only JPEG or PNG
                format is supported <span style={{ color: "red" }}>*</span>
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={8} xl={8}>
            <ReusableFormFields
              inputFields={inputFields}
              control={control}
              errors={errors}
              register={register}
              setValue={setValue}
            />

            {/* Submit Button */}
            <Box sx={styles.submitGap}>
              <Grid container spacing={2} item xs={12}>
                <Grid item xs={6}>
                  <CustomButton
                    text="Submit"
                    size="medium"
                    type="submit"
                    buttonStyle="StyledButton"
                    disabled={addProductState?.inProgress}
                    loading={addProductState?.inProgress}
                  />
                </Grid>
                <Grid item xs={6}>
                  <CustomButton
                    text="Cancel"
                    variant="contained"
                    size="medium"
                    fullWidth
                    buttonStyle="style1"
                    onClick={handleCancel}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
      </form>

      {/* Snackbar */}
      <CommonSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity={snackbarSeverity}
        onClose={handleSnackbarClose}
      />

      {/* Dialog */}
      <CommonDialog
        open={dialogOpen}
        isSuccess={isSuccess}
        onClose={handleDialogClose}
        messageSuccess={dialogMessage}
        messageError={dialogMessage}
      />

      {/* Image Popup Modal with Scrollbar */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <Scrollbars style={styles.reactScroll}>
          <DialogContent>
            <img
              src={imagePreview}
              alt="Full Image"
              style={styles.reactScrollStyle}
            />
          </DialogContent>
        </Scrollbars>
      </Dialog>
    </Container>
  );
};

export default AddProduct;
